import { makeAutoObservable, runInAction } from 'mobx';

import { cmApi } from '@src/api';
import { preloaderStore } from '@stores/preloaderStore';
import type { CmApi, ControlApplicabilityDto } from '@src/api';

import { controlApplicabilityStore } from '../controlApplicabilityStore';

class ControlApplicabilityEditStore {
  controlApplicability: ControlApplicabilityDto | null;

  isNew = false;

  editMode = false;

  api: CmApi;

  hiddenEdit = false;

  hiddenDelete = false;

  hiddenClean = false;

  hiddenSave = false;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    this.controlApplicability = null;

    makeAutoObservable(this);
  }

  get isEditMode() {
    return this.editMode || this.isNew;
  }

  setNew = (isNew = true) => {
    this.isNew = isNew;
    this.controlApplicability = null;
  };

  setEditMode = (editMode = true) => {
    this.editMode = editMode;
  };

  save = async (control: ControlApplicabilityDto) => {
    preloaderStore.preloader(true);
    const { data } = await (this.isNew
      ? this.api.controlApplicability.addControlApplicability(control)
      : this.api.controlApplicability.updateControlApplicability(control));

    if (data) {
      controlApplicabilityStore.setIsChanged(true);

      if (this.isNew) {
        preloaderStore.preloader(false);
        return { orgCode: data.orgCode, ctrlVersCode: data.ctrlVersCode };
      }
      runInAction(() => {
        this.controlApplicability = data;
        this.setEditMode(false);
      });
    }
    preloaderStore.preloader(false);
    return undefined;
  };

  delete = async () => {
    if (this.controlApplicability) {
      await this.api.controlApplicability.deleteControlApplicabilityByCodes(
        this.controlApplicability.ctrlVersCode ?? '',
        this.controlApplicability.orgCode ?? '',
      );
      controlApplicabilityStore.setIsChanged(true);
    }
  };

  fetch = async (ctrlVersCode?: string, orgCode?: string): Promise<void> => {
    if (!ctrlVersCode || !orgCode) {
      return;
    }
    preloaderStore.preloader(true);
    runInAction(() => {
      this.setEditMode(false);
      this.setNew(false);
    });

    const { data } = await this.api.controlApplicability.getControlApplicabilityByCodes(ctrlVersCode, orgCode);

    runInAction(() => {
      this.controlApplicability = data;
    });

    preloaderStore.preloader(false);
  };

  setHiddenEdit = (hiddenEdit = true): void => {
    this.hiddenEdit = hiddenEdit;
  };

  setHiddenDelete = (hiddenDelete = true): void => {
    this.hiddenDelete = hiddenDelete;
  };

  setHiddenClean = (hiddenClean = true): void => {
    this.hiddenClean = hiddenClean;
  };

  setHiddenSave = (hiddenSave = true): void => {
    this.hiddenSave = hiddenSave;
  };
}

export const controlApplicabilityEditStore = new ControlApplicabilityEditStore({ api: cmApi });
